/**
 * State for the canvas agent chat — the single canvas chat surface.
 *
 * Owns three things:
 *  - **Session navigation**: an in-memory stack (push/replace/back). `currentSessionId` is the
 *    top of the stack (or null) — it means "an explicit session is active"; non-UI consumers
 *    (onboarding `isAgentChatOpen`, loading-nodes, deck/image steps) rely on that meaning.
 *  - **Surface state**: `open` (the chat window is up) vs closed (just the FAB launcher), plus a
 *    persisted `sizeClass` (`windowed` floating panel vs `full-height` docked). `openSession` /
 *    `expand` open the window; `collapse()` minimizes back to the FAB.
 *  - **Composer model**: the active canvas session for the landing (`activeSessionId`),
 *    create-on-send (`send`, canvas-class), and the landing-dismiss flag
 *    (`hasStarted` ← `canvas.metadata.composer_started` OR any existing canvas chat session).
 *
 * Under `CanvasProvider` this remounts when `canvasId` changes, so all state resets per canvas.
 */

import {
  useCanvasById,
  useCanvasFlowSessions,
  useUpdateCanvas,
} from "@/api/canvas";
import { useCreateFlowSession } from "@/api/flow_sessions";
import { usePostFlowMessage } from "@/api/messages";
import Sentry from "@/integrations/Sentry";
import {
  FLOW_SESSION_MODES,
  type Canvas,
  type FlowSession,
  type ReferencedEntity,
} from "@cohesive/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useLocalStorageState } from "ahooks";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import toast from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";
import { useCanvasReasoningEffort } from "../hooks/useCanvasReasoningEffort";
import { useCanvasRoute } from "./CanvasRouteContext";

export type CanvasFloatingSessionHistory = "push" | "replace";

/** Window size class: a small floating panel vs a full-height docked panel. */
export type CanvasChatSizeClass = "windowed" | "full-height";

/** Result of a composer send (mirrors the shared MessageInput send contract). */
export interface CanvasChatSendResult {
  messageSent: boolean;
}

export interface CanvasFloatingChatContextValue {
  /** Top-of-stack expanded session id, or null. Means "an explicit session is active". */
  currentSessionId: string | null;
  canGoBack: boolean;
  /** Second arg defaults to `"push"` — pass `"replace"` only when swapping the top session. Also expands the window. */
  openSession: (
    sessionId: string,
    history?: CanvasFloatingSessionHistory,
  ) => void;
  goBack: () => void;
  /** Clears the stack and closes the window back to the FAB (does NOT unmount the surface). */
  closeChat: () => void;

  /** True when the chat window is up; false shows only the FAB launcher. */
  open: boolean;
  /** Open the window onto a session (defaults to the active session, minting one if none). */
  expand: (sessionId?: string) => void;
  /** Minimize the window back to the FAB, keeping the session on the stack. */
  collapse: () => void;
  /** windowed (floating panel) vs full-height (docked). Persisted per canvas. */
  sizeClass: CanvasChatSizeClass;
  /** Flip between the windowed and full-height size classes. */
  toggleSizeClass: () => void;

  /** The canvas session the always-present bar / landing acts on (mirrors the open session, else most-recent). */
  activeSessionId: string | null;
  /** True once the canvas has any user chat session (or `composer_started`); dismisses the landing. */
  hasStarted: boolean;
  isSubmitting: boolean;
  /** Posts to the active canvas session, creating a canvas-class session on the first send. */
  send: (
    content: string,
    referencedEntities: ReferencedEntity[],
    agentReasoningEffort?: string,
  ) => Promise<CanvasChatSendResult>;

  /**
   * Pixels to subtract from the bottom toolbar row `maxWidth` so it stays left of
   * the chat (shell width + `CANVAS_FLOATING_CHAT_TOOLBAR_RIGHT_MARGIN`).
   */
  bottomToolbarRightClearancePx: number;
  /** Called from `CanvasFloatingChatPanel` via ResizeObserver. */
  setBottomToolbarRightClearancePx: Dispatch<SetStateAction<number>>;
  /**
   * Horizontal offset for the top-right share + controls row when the chat shell
   * overlaps that band (full-height dock, or a windowed panel resized upward).
   */
  topControlsRightClearancePx: number;
  /** Called from `CanvasFloatingChatPanel` via ResizeObserver. */
  setTopControlsRightClearancePx: Dispatch<SetStateAction<number>>;
}

const CanvasFloatingChatContext =
  createContext<CanvasFloatingChatContextValue | null>(null);

/**
 * Horizontal space reserved beside the floating chat (`right-2`) plus a small gap
 * so the bottom canvas toolbar stays clear of the popover.
 */
export const CANVAS_FLOATING_CHAT_TOOLBAR_RIGHT_MARGIN = 16;

/**
 * Distance from the React Flow wrapper top to the bottom of the share + controls
 * row (`top-4` inset + horizontal toolbar chrome). Used to detect when a
 * windowed chat panel resized upward would overlap those controls.
 */
export const CANVAS_TOP_CONTROLS_BAND_BOTTOM_PX = 56;

/** Mirrors the session visible in the open floating chat panel (not the FAB-only stack). */
export const CANVAS_FLOATING_CHAT_ACTIVE_SESSION_EVENT =
  "canvas:floating-chat-active-session";

const floatingChatActiveSessionByCanvasId = new Map<string, string | null>();

/** Last emitted visible session for a canvas — for subscribers outside the provider tree. */
export function readCanvasFloatingChatActiveSession(
  canvasId: string,
): string | null {
  return floatingChatActiveSessionByCanvasId.get(canvasId) ?? null;
}

function emitCanvasFloatingChatActiveSession(detail: {
  canvasId: string;
  flowSessionId: string | null;
}) {
  floatingChatActiveSessionByCanvasId.set(
    detail.canvasId,
    detail.flowSessionId,
  );
  window.dispatchEvent(
    new CustomEvent(CANVAS_FLOATING_CHAT_ACTIVE_SESSION_EVENT, { detail }),
  );
}

/** For callers outside React (e.g. explorer pane) — same event as legacy `canvas:open-session-pane`. */
export function emitCanvasOpenFloatingSession(detail: {
  canvasId: string;
  flowSessionId: string;
  /** Defaults to `"push"` when omitted. */
  history?: CanvasFloatingSessionHistory;
}) {
  window.dispatchEvent(
    new CustomEvent("canvas:open-session-pane", {
      detail: {
        canvasId: detail.canvasId,
        flowSessionId: detail.flowSessionId,
        history: detail.history ?? "push",
      },
    }),
  );
}

export function useCanvasFloatingChat(): CanvasFloatingChatContextValue {
  const ctx = useContext(CanvasFloatingChatContext);
  if (!ctx) {
    throw new Error(
      "useCanvasFloatingChat must be used within CanvasFloatingChatProvider",
    );
  }
  return ctx;
}

/** Canvas chat sessions the user authored directly (exclude sub-agent/action-item runs), newest first. */
function selectUserSessions(
  sessions: FlowSession[] | undefined,
): FlowSession[] {
  if (!sessions) return [];
  return [...sessions]
    .filter((s) => !s.action_item_id)
    .sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1));
}

export function CanvasFloatingChatProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { canvasId } = useCanvasRoute();
  const { data: canvas } = useCanvasById(canvasId);
  const { data: rawSessions } = useCanvasFlowSessions(canvasId);
  const createFlowSession = useCreateFlowSession();
  const postMessage = usePostFlowMessage();
  const updateCanvas = useUpdateCanvas();
  const queryClient = useQueryClient();
  const [reasoningEffort] = useCanvasReasoningEffort(canvasId ?? "");

  const [stack, setStack] = useState<string[]>([]);
  // The window opens closed on every load (FAB only); never persisted.
  const [open, setOpen] = useState(false);
  const [bottomToolbarRightClearancePx, setBottomToolbarRightClearancePx] =
    useState(0);
  const [topControlsRightClearancePx, setTopControlsRightClearancePx] =
    useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Window size class persists per canvas so a user's windowed/full-height
  // preference survives reloads (unlike `open`, which always starts closed).
  const [sizeClassRaw, setSizeClassRaw] =
    useLocalStorageState<CanvasChatSizeClass>(
      `canvas_floating_chat_size_class_${canvasId ?? "__none__"}`,
      { defaultValue: "windowed" },
    );
  const sizeClass = sizeClassRaw ?? "windowed";
  const toggleSizeClass = useCallback(
    () =>
      setSizeClassRaw((prev) =>
        (prev ?? "windowed") === "windowed" ? "full-height" : "windowed",
      ),
    [setSizeClassRaw],
  );

  const currentSessionId = stack.length > 0 ? stack[stack.length - 1]! : null;
  const canGoBack = stack.length > 1;

  const sessions = useMemo(
    () => selectUserSessions(rawSessions),
    [rawSessions],
  );

  // The session the always-present bar / landing acts on: mirror the open session so
  // collapsing keeps context, else fall back to the most-recent canvas session.
  const activeSessionId = useMemo<string | null>(() => {
    if (currentSessionId) return currentSessionId;
    return sessions[0]?.id ?? null;
  }, [currentSessionId, sessions]);

  // The landing is dismissed once the canvas has *any* user chat session — not
  // just when the optimistic `composer_started` flag is set. Relying on the flag
  // alone let the landing reappear when a canvas refetch returned server data
  // before the flag persisted; the presence of a session is the durable signal.
  const hasStarted =
    (canvas?.metadata?.composer_started ?? false) || sessions.length > 0;

  const markStarted = useCallback(() => {
    if (!canvasId || canvas?.metadata?.composer_started) return;
    const nextMetadata = { ...canvas?.metadata, composer_started: true };
    queryClient.setQueryData<Canvas>(["canvas", canvasId], (prev) =>
      prev ? { ...prev, metadata: nextMetadata } : prev,
    );
    updateCanvas.mutate({ id: canvasId, data: { metadata: nextMetadata } });
  }, [canvasId, canvas?.metadata, queryClient, updateCanvas]);

  const createCanvasSession = useCallback(async (): Promise<string> => {
    const session = await createFlowSession.mutateAsync({
      mode: FLOW_SESSION_MODES.CANVAS_CHAT,
      class: "canvas",
      canvas_id: canvasId,
    });
    return session.id;
  }, [createFlowSession, canvasId]);

  const openSession = useCallback(
    (sessionId: string, history: CanvasFloatingSessionHistory = "push") => {
      if (!sessionId) return;
      setStack((prev) => {
        if (history === "replace") {
          if (prev.length === 0) return [sessionId];
          return [...prev.slice(0, -1), sessionId];
        }
        if (prev.length === 0) return [sessionId];
        const top = prev[prev.length - 1];
        if (top === sessionId) return prev;
        return [...prev, sessionId];
      });
      // Opening a session always surfaces the window.
      setOpen(true);
    },
    [],
  );

  const goBack = useCallback(() => {
    setStack((prev) => (prev.length <= 1 ? [] : prev.slice(0, -1)));
  }, []);

  // Close to the FAB and clear session history; the surface stays mounted (NOT unmounted).
  const closeChat = useCallback(() => {
    setStack([]);
    setOpen(false);
  }, []);

  // Minimize to the FAB but keep the session stack so reopening restores context.
  const collapse = useCallback(() => setOpen(false), []);

  const expand = useCallback(
    (sessionId?: string) => {
      // Guard against event handlers that pass a click event as the arg
      // (e.g. `onClick={expand}`): only a real string id selects a session.
      const requested = typeof sessionId === "string" ? sessionId : undefined;
      const target = requested ?? currentSessionId ?? activeSessionId;
      if (target) {
        openSession(target);
        return;
      }
      // No session yet — mint a canvas-agent session, then expand onto it.
      if (!canvasId) return;
      void (async () => {
        try {
          const id = await createCanvasSession();
          openSession(id);
        } catch (err) {
          Sentry.captureException(err);
          toast.error("Failed to open chat");
        }
      })();
    },
    [
      currentSessionId,
      activeSessionId,
      openSession,
      canvasId,
      createCanvasSession,
    ],
  );

  const send = useCallback(
    async (
      content: string,
      referencedEntities: ReferencedEntity[],
      agentReasoningEffort?: string,
    ): Promise<CanvasChatSendResult> => {
      const text = content.trim();
      if (!canvasId || !text) return { messageSent: false };

      setIsSubmitting(true);
      try {
        let sessionId = activeSessionId;
        if (!sessionId) {
          sessionId = await createCanvasSession();
        }
        await postMessage.mutateAsync({
          flowSessionId: sessionId,
          id: uuidv4(),
          content: text,
          referenced_entities: referencedEntities,
          reasoning_effort: agentReasoningEffort ?? reasoningEffort,
        });
        markStarted();
        // Surface the window so the just-sent message + reply are visible
        // (the landing's composer is the only remaining caller of `send`).
        openSession(sessionId);
        return { messageSent: true };
      } catch (error) {
        console.error("[CanvasFloatingChat] Failed to send:", error);
        return { messageSent: false };
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      canvasId,
      activeSessionId,
      createCanvasSession,
      postMessage,
      reasoningEffort,
      markStarted,
      openSession,
    ],
  );

  // Chats rail lives outside this provider; broadcast the session actually shown
  // in the open panel (clear when minimized to FAB or fully closed).
  useEffect(() => {
    if (!canvasId) return;
    emitCanvasFloatingChatActiveSession({
      canvasId,
      flowSessionId: open && currentSessionId ? currentSessionId : null,
    });
  }, [canvasId, open, currentSessionId]);

  const value = useMemo(
    () => ({
      currentSessionId,
      canGoBack,
      openSession,
      goBack,
      closeChat,
      open,
      expand,
      collapse,
      sizeClass,
      toggleSizeClass,
      activeSessionId,
      hasStarted,
      isSubmitting,
      send,
      bottomToolbarRightClearancePx,
      setBottomToolbarRightClearancePx,
      topControlsRightClearancePx,
      setTopControlsRightClearancePx,
    }),
    [
      currentSessionId,
      canGoBack,
      openSession,
      goBack,
      closeChat,
      open,
      expand,
      collapse,
      sizeClass,
      toggleSizeClass,
      activeSessionId,
      hasStarted,
      isSubmitting,
      send,
      bottomToolbarRightClearancePx,
      topControlsRightClearancePx,
    ],
  );

  return (
    <CanvasFloatingChatContext.Provider value={value}>
      {children}
    </CanvasFloatingChatContext.Provider>
  );
}

const PUBLIC_VIEW_CHAT_VALUE: CanvasFloatingChatContextValue = {
  currentSessionId: null,
  canGoBack: false,
  openSession: () => {},
  goBack: () => {},
  closeChat: () => {},
  open: false,
  expand: () => {},
  collapse: () => {},
  sizeClass: "windowed",
  toggleSizeClass: () => {},
  activeSessionId: null,
  hasStarted: false,
  isSubmitting: false,
  send: async () => ({ messageSent: false }),
  bottomToolbarRightClearancePx: 0,
  setBottomToolbarRightClearancePx: () => {},
  topControlsRightClearancePx: 0,
  setTopControlsRightClearancePx: () => {},
};

/**
 * No-op floating chat for the public canvas-view SPA. EntityNode still calls
 * `useCanvasFloatingChat`; chat UI is never mounted on the public surface.
 */
export function CanvasFloatingChatStubProvider({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <CanvasFloatingChatContext.Provider value={PUBLIC_VIEW_CHAT_VALUE}>
      {children}
    </CanvasFloatingChatContext.Provider>
  );
}
